import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import CartService from '../services/cart.service';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { useNotification } from './NotificationContext';
import { useToast } from './ToastContext';
import logger from '../utils/logger';

// Create the context
const CartContext = createContext();

// Create a provider component
export const CartProvider = ({ children }) => {
  const [cartItems, setCartItemsState] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated } = useAuth();
  const { showSuccess, showError } = useNotification();
  const toast = useToast();
  
  // localStorage cart management
  const CART_STORAGE_KEY = 'mmart_guest_cart';
  
  // Save cart to localStorage (for guest users)
  const saveCartToLocalStorage = (items) => {
    try {
      if (!isAuthenticated && items) {
        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
        logger.debug('Cart saved to localStorage:', items);
      }
    } catch (error) {
      logger.error('Failed to save cart to localStorage:', error);
    }
  };
  
  // Load cart from localStorage (for guest users)
  const loadCartFromLocalStorage = () => {
    try {
      if (!isAuthenticated) {
        const savedCart = localStorage.getItem(CART_STORAGE_KEY);
        if (savedCart) {
          const parsedCart = JSON.parse(savedCart);
          logger.debug('Cart loaded from localStorage:', parsedCart);
          return parsedCart;
        }
      }
      return [];
    } catch (error) {
      logger.error('Failed to load cart from localStorage:', error);
      return [];
    }
  };
  
  // Clear cart from localStorage
  const clearCartFromLocalStorage = () => {
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
      logger.debug('Cart cleared from localStorage');
    } catch (error) {
      logger.error('Failed to clear cart from localStorage:', error);
    }
  };
  
  // Custom setCartItems that automatically syncs to localStorage for guest users
  const setCartItems = (items) => {
    // Handle both direct values and updater functions
    const newItems = typeof items === 'function' ? items(cartItems) : items;
    
    // Update state
    setCartItemsState(newItems);
    
    // Sync to localStorage for guest users
    saveCartToLocalStorage(newItems);
    
    logger.debug('Cart items updated and synced:', newItems);
  };
  
  // Refs for delayed API calls (used by clearCart)
  const apiCallTimeoutRef = useRef(null);
  const pendingOperationsRef = useRef({});
  
  // Request deduplication and debouncing for rapid-click fix
  const pendingRequestsRef = useRef({}); // Track pending API requests
  const debounceTimersRef = useRef({}); // Track debounce timers
  
  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (apiCallTimeoutRef.current) {
        clearTimeout(apiCallTimeoutRef.current);
      }
      // Clear all debounce timers
      Object.values(debounceTimersRef.current).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
      // Clear pending requests
      pendingRequestsRef.current = {};
      debounceTimersRef.current = {};
    };
  }, []);
  
  // Format price in Naira
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2
    }).format(price);
  };

  // Calculate cart totals
  const calculateTotals = () => {
    const subtotal = cartItems.reduce((total, item) => {
      // Skip calculation if item, measurement, or product is null/undefined
      if (!item) return total;
      
      let itemPrice = 0;
      
      if (item.measurement && (item.measurement.sale_price || item.measurement.price)) {
        // Use measurement price if available
        itemPrice = item.measurement.sale_price || item.measurement.price;
      } else if (item.product && (item.product.sale_price || item.product.base_price)) {
        // Use product price if available
        itemPrice = item.product.sale_price || item.product.base_price;
      }
      
      return total + (itemPrice * (item.quantity || 1));
    }, 0);

    return {
      subtotal,
      formattedSubtotal: formatPrice(subtotal),
      itemCount: cartItems.reduce((count, item) => count + (item ? item.quantity || 0 : 0), 0)
    };
  };

  // Handle authentication state changes and cart synchronization
  const [previousAuthState, setPreviousAuthState] = useState(null);
  
  useEffect(() => {
    const handleAuthStateChange = async () => {
      if (isAuthenticated && previousAuthState === false) {
        // User just logged in - sync guest cart with backend
        await syncGuestCartWithBackend();
      } else if (!isAuthenticated && previousAuthState === true) {
        // User just logged out - clear authenticated cart data and load guest cart
        await handleLogout();
      } else if (isAuthenticated && previousAuthState === null) {
        // Initial load for authenticated user
        await fetchCartItems();
      } else if (!isAuthenticated && previousAuthState === null) {
        // Initial load for guest user
        await fetchCartItems();
      }
      
      setPreviousAuthState(isAuthenticated);
    };
    
    // OPTIMIZATION: Don't block UI rendering - handle cart loading asynchronously
    handleAuthStateChange().catch(err => {
      console.error('Cart initialization error:', err);
      // UI still renders even if cart fails to load
    });
  }, [isAuthenticated]);
  
  // Sync guest cart with backend when user logs in
  const syncGuestCartWithBackend = async () => {
    try {
      setLoading(true);
      logger.debug('🔄 Starting guest cart sync with backend');
      
      // Get guest cart from localStorage
      const guestCart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY) || '[]');
      logger.debug('Guest cart from localStorage:', guestCart);
      
      if (guestCart.length === 0) {
        // No guest cart items, just fetch authenticated cart
        await fetchCartItems();
        return;
      }
      
      // Get authenticated cart from backend
      const response = await CartService.getCartItems();
      const backendCart = response?.cart_items || [];
      logger.debug('Backend cart:', backendCart);
      
      // Merge carts intelligently
      const mergedCart = mergeCartItems(guestCart, backendCart);
      logger.debug('Merged cart:', mergedCart);
      
      // Sync merged cart to backend
      if (mergedCart.length > 0) {
        const syncResponse = await CartService.syncCart(mergedCart);
        if (syncResponse?.cart_items) {
          setCartItems(syncResponse.cart_items);
        } else {
          setCartItems(mergedCart);
        }
      } else {
        setCartItems([]);
      }
      
      // Clear guest cart from localStorage (no longer needed)
      clearCartFromLocalStorage();
      
      logger.debug('✅ Guest cart successfully synced with backend');
      showSuccess('Your cart has been synchronized');
      
    } catch (error) {
      logger.error('❌ Failed to sync guest cart with backend:', error);
      // Fallback: just load the backend cart
      await fetchCartItems();
      showError('Failed to sync cart, but your account cart has been loaded');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle user logout
  const handleLogout = async () => {
    try {
      // Check if there's a pending payment - if so, don't clear cart
      const pendingPayment = localStorage.getItem('pendingPayment');
      const pendingOrder = localStorage.getItem('pendingOrder');
      
      if (pendingPayment || pendingOrder) {
        logger.debug('🛑 Payment in progress - skipping cart clear during auth state change');
        // Don't clear cart if payment is in progress
        return;
      }
      
      logger.debug('🚪 Handling user logout');
      
      // Clear authenticated cart data
      setCartItems([]);
      
      // Load fresh guest cart (empty initially)
      const guestCart = loadCartFromLocalStorage();
      setCartItems(guestCart);
      
      logger.debug('✅ Logout handled, switched to guest cart');
      
    } catch (error) {
      logger.error('❌ Error during logout:', error);
      // Ensure we have a clean state
      setCartItems([]);
    }
  };
  
  // Intelligent cart merging logic
  const mergeCartItems = (guestCart, backendCart) => {
    const merged = [...backendCart];
    
    guestCart.forEach(guestItem => {
      const existingItem = merged.find(item => 
        item.product_id === guestItem.product_id && 
        item.product_measurement_id === guestItem.product_measurement_id
      );
      
      if (existingItem) {
        // Merge quantities for same product + measurement
        existingItem.quantity += guestItem.quantity;
        logger.debug(`Merged quantities for product ${guestItem.product_id}: ${existingItem.quantity}`);
      } else {
        // Add new item from guest cart
        merged.push({
          ...guestItem,
          id: `temp_${guestItem.product_id}_${guestItem.product_measurement_id || 'default'}`
        });
        logger.debug(`Added new item from guest cart: product ${guestItem.product_id}`);
      }
    });
    
    return merged;
  };

  // Fetch cart items from API (authenticated users) or localStorage (guest users)
  const fetchCartItems = async () => {
    setLoading(true);
    setError(null);
    try {
      if (isAuthenticated) {
        // Authenticated user: fetch from backend
        const response = await CartService.getCartItems();
        logger.debug('Cart items from API:', response);
        
        if (response && response.cart_items) {
          logger.debug('Setting cart items from backend:', response.cart_items);
          setCartItems(response.cart_items || []);
        } else {
          setCartItems([]);
        }
      } else {
        // Guest user: load from localStorage
        const guestCart = loadCartFromLocalStorage();
        logger.debug('Setting cart items from localStorage:', guestCart);
        setCartItems(guestCart);
      }
    } catch (err) {
      logger.error('Failed to fetch cart items:', err);
      setError(err);
      
      // For guest users, fallback to localStorage even on API error
      if (!isAuthenticated) {
        const guestCart = loadCartFromLocalStorage();
        setCartItems(guestCart);
      }
      
      // Show specific error message from backend or fallback to generic message
      const errorMessage = err.message || 'Failed to fetch cart items. Please try again.';
      showError(errorMessage);
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Add item to cart with optimistic update and request deduplication
  const addToCart = async (product, quantity = 1, measurementId = null) => {
    setError(null);
    
    // Create a key for this product + measurement combination
    const productKey = `${product.id}_${measurementId || 'default'}`;
    
    // Store original items for rollback (declare outside try block)
    const originalItems = [...cartItems];
    
    // Cancel any existing debounce timer for this product
    if (debounceTimersRef.current[productKey]) {
      clearTimeout(debounceTimersRef.current[productKey]);
      delete debounceTimersRef.current[productKey];
    }
    
    try {
      
      // Create the new cart item for optimistic update
      const newItem = {
        id: `temp_${productKey}`,
        product_id: product.id,
        quantity,
        product_measurement_id: measurementId,
        product: product,
        measurement: product.measurements?.find(m => m.id === measurementId)
      };
      
      // Optimistically update the UI immediately
      setCartItems(prevItems => {
        const existingItem = prevItems.find(item => 
          item.product_id === product.id && 
          item.product_measurement_id === measurementId
        );
        
        if (existingItem) {
          return prevItems.map(item => 
            item.product_id === product.id && 
            item.product_measurement_id === measurementId
              ? { ...item, quantity: item.quantity + quantity }
              : item
          );
        } else {
          return [...prevItems, newItem];
        }
      });
      
      // Show sliding toast notification
      toast.showSuccess(`Added ${product.name} to cart`, 3000);
      
      // Track Facebook Pixel AddToCart event
      if (window.fbq) {
        const eventData = {
          content_name: product.name,
          content_ids: [product.id],
          content_type: 'product',
          value: parseFloat(product.sale_price || product.base_price) * quantity,
          currency: 'NGN'
        };
        window.fbq('track', 'AddToCart', eventData);
        console.log('Facebook Pixel AddToCart tracked:', eventData);
      } else {
        console.warn('Facebook Pixel not loaded - AddToCart not tracked');
      }
      
      // Handle authenticated vs guest users differently
      if (isAuthenticated) {
        // For authenticated users, debounce API calls to prevent race conditions
        const currentQuantity = cartItems.find(item => 
          item.product_id === product.id && 
          item.product_measurement_id === measurementId
        )?.quantity || 0;
        
        const finalQuantity = currentQuantity + quantity;
        
        // Set up debounced API call
        debounceTimersRef.current[productKey] = setTimeout(async () => {
          try {
            logger.debug(`Making debounced API call for ${productKey} with quantity:`, finalQuantity);
            
            const response = await CartService.addToCart({
              product_id: product.id,
              quantity: quantity,
              product_measurement_id: measurementId
            });
            
            // Use the full cart_items array from our Phase 1 backend fix
            if (response && response.cart_items) {
              logger.debug('Updating cart with backend cart_items after debounced add:', response.cart_items);
              setCartItems(response.cart_items);
            } else {
              // Fallback: if no cart_items in response, refresh from server
              logger.warn('No cart_items in addToCart response, refreshing cart');
              await fetchCartItems();
            }
            
            // Clean up the timer reference
            delete debounceTimersRef.current[productKey];
            
          } catch (error) {
            logger.error('Debounced API call failed:', error);
            // Clean up the timer reference
            delete debounceTimersRef.current[productKey];
            throw error;
          }
        }, 300); // 300ms debounce delay
        
        logger.debug(`Debounced API call scheduled for ${productKey}`);
        
      } else {
        // Guest user: No API call needed, localStorage already updated by setCartItems
        logger.debug('Guest user: Cart item added to localStorage only');
        // The optimistic update above already updated both state and localStorage
        // No further action needed for guest users
        return { success: true, message: 'Item added to guest cart' };
      }
    } catch (err) {
      console.error('Failed to add item to cart:', err);
      
      // Rollback to original state (standardized approach)
      setCartItems(originalItems);
      
      // Standardized error handling
      if (err.response?.status === 422) {
        // Validation error (e.g., insufficient stock)
        const errorMessage = err.response?.data?.message || 'Unable to add item to cart';
        showError(errorMessage);
      } else if (err.response?.status === 404) {
        // Product not found
        showError('Product is no longer available');
      } else {
        // Generic error
        showError(err.message || 'Failed to add item to cart');
      }
      
      throw err;
    }
  };

  // Update cart item quantity with optimistic update (ProductDetail pattern)
  const updateCartItem = async (itemId, quantity) => {
    logger.debug('updateCartItem called with:', { itemId, quantity });
    
    // Find the item being updated
    const currentItem = cartItems.find(item => item.id === itemId);
    if (!currentItem) {
      logger.warn(`Cart item ${itemId} not found`);
      return;
    }
    
    // Proactive stock validation for better UX
    const product = currentItem.product;
    const availableStock = product?.stock_quantity || 0;
    
    if (quantity > availableStock) {
      // Show user-friendly stock limitation message
      const productName = product?.name || 'this item';
      toast.showError(
        `Only ${availableStock} ${availableStock === 1 ? 'item' : 'items'} of ${productName} available in stock`,
        4000
      );
      
      // Set quantity to maximum available stock
      const maxQuantity = Math.max(1, availableStock);
      if (currentItem.quantity !== maxQuantity) {
        logger.debug(`Adjusting quantity to maximum available: ${maxQuantity}`);
        // Update to max available stock instead of failing
        return updateCartItem(itemId, maxQuantity);
      }
      
      return; // Don't proceed if already at max stock
    }
    
    // Prevent multiple rapid updates for the same item (ProductDetail pattern)
    const updateKey = `updating_${itemId}`;
    if (pendingRequestsRef.current[updateKey]) {
      logger.debug(`Update already in progress for item ${itemId}, ignoring`);
      return;
    }
    
    let originalItems = null;
    
    try {
      // Store original items for rollback
      originalItems = [...cartItems];
      
      // Optimistically update the UI immediately
      setCartItems(prevItems =>
        prevItems.map(item =>
          item.id === itemId ? { ...item, quantity } : item
        )
      );
      
      // Handle authenticated vs guest users differently
      if (isAuthenticated) {
        // Mark this item as being updated (prevents rapid clicks)
        pendingRequestsRef.current[updateKey] = true;
        
        logger.debug(`Making API call for item ${itemId} with quantity:`, quantity);
        
        const response = await CartService.updateCartItem(itemId, quantity);
        
        // Use the full cart_items array from our Phase 1 backend fix
        if (response && response.cart_items) {
          logger.debug('Updating cart with backend cart_items after update:', response.cart_items);
          setCartItems(response.cart_items);
        } else {
          // Fallback: if no cart_items in response, refresh from server
          logger.warn('No cart_items in updateCartItem response, refreshing cart');
          await fetchCartItems();
        }
        
      } else {
        // Guest user: Update localStorage immediately
        const updatedItems = cartItems.map(item =>
          item.id === itemId ? { ...item, quantity } : item
        );
        localStorage.setItem('cart', JSON.stringify(updatedItems));
        logger.debug('Guest cart updated in localStorage');
      }
      
    } catch (error) {
      logger.error('Error in updateCartItem:', error);
      
      // Rollback optimistic update on error
      if (originalItems) {
        setCartItems(originalItems);
      }
      
      // Enhanced error handling with user-friendly messages
      if (error.response?.status === 422) {
        // Stock validation error - provide helpful feedback
        const errorMessage = error.response?.data?.message || 'Not enough stock available';
        const productName = currentItem.product?.name || 'this item';
        
        if (errorMessage.includes('stock')) {
          toast.showError(
            `Sorry, we don't have enough ${productName} in stock. Please reduce the quantity.`,
            4000
          );
        } else {
          toast.showError(errorMessage, 3000);
        }
        
        // Try to get updated stock info and adjust quantity
        try {
          await fetchCartItems(); // Refresh to get latest stock info
        } catch (refreshError) {
          logger.warn('Failed to refresh cart after stock error:', refreshError);
        }
        
        return; // Don't throw, we've handled it gracefully
      } else if (error.response?.status === 404) {
        logger.warn(`Cart item ${itemId} not found, removing from frontend state`);
        setCartItems(prevItems => prevItems.filter(item => item.id !== itemId));
        toast.showError('This item is no longer available and has been removed from your cart', 3000);
        return;
      }
      
      // For other errors, show generic message but don't crash
      toast.showError('Unable to update quantity. Please try again.', 3000);
      logger.error('Unhandled updateCartItem error:', error);
      
    } finally {
      // Always clean up the pending request flag
      delete pendingRequestsRef.current[updateKey];
    }
  };

  // Remove item from cart with optimistic update
  const removeCartItem = async (itemId) => {
    setError(null);
    let originalItems;
    
    try {
      // Store original items for rollback
      originalItems = [...cartItems];
      
      // Optimistically remove the item from UI
      setCartItems(prevItems => prevItems.filter(item => item.id !== itemId));
      
      // Handle authenticated vs guest users differently
      if (isAuthenticated) {
        // Authenticated user: Make API call
        const response = await CartService.removeCartItem(itemId);
        
        // Use the full cart_items array from our Phase 1 backend fix
        if (response && response.cart_items) {
          logger.debug('Updating cart with backend cart_items after removal:', response.cart_items);
          setCartItems(response.cart_items);
        } else {
          // Fallback: if no cart_items in response, refresh from server
          logger.warn('No cart_items in removeCartItem response, refreshing cart');
          await fetchCartItems();
        }
        return response;
      } else {
        // Guest user: No API call needed, localStorage already updated by setCartItems
        logger.debug('Guest user: Cart item removed from localStorage only');
        return { success: true, message: 'Item removed from guest cart' };
      }
    } catch (err) {
      console.error('Failed to remove cart item:', err);
      
      // Handle 404 errors (item already removed or doesn't exist)
      if (err.response?.status === 404) {
        logger.warn('Cart item not found, keeping it removed from frontend state');
        showError('Item was already removed from your cart');
      } else {
        // Rollback to original state for other errors
        setCartItems(originalItems);
        showError(err.message || 'Failed to remove item from cart');
      }
      
      throw err;
    }
  };

  // Clear cart with optimistic updates
  const clearCart = async () => {
    // Cancel any pending API calls
    if (apiCallTimeoutRef.current) {
      clearTimeout(apiCallTimeoutRef.current);
    }
    
    // Clear all pending operations
    pendingOperationsRef.current = {};
    
    // Store original cart items for potential rollback
    const originalCartItems = [...cartItems];
    
    // Immediately clear the cart in the UI for better user experience
    setCartItems([]);
    
    // Clear coupon-related localStorage items immediately
    localStorage.removeItem('appliedCoupon');
    localStorage.removeItem('discountAmount');
    localStorage.removeItem('shippingFee');
    localStorage.removeItem('deliveryInfo');
    localStorage.removeItem('orderDetails');
    
    try {
      // Return a promise that will be resolved after the API call
      return new Promise((resolve, reject) => {
        // Set a timeout to execute the API call after a delay
        apiCallTimeoutRef.current = setTimeout(async () => {
          try {
            setLoading(true);
            const response = await CartService.clearCart();
            setLoading(false);
            resolve(response);
          } catch (err) {
            console.error('Failed to clear cart:', err);
            setError(err);
            setLoading(false);
            
            // Restore original cart items on error
            setCartItems(originalCartItems);
            
            // Show specific error message from backend or fallback to generic message
            const errorMessage = err.response?.data?.message || err.message || 'Failed to clear cart. Please try again.';
            showError(errorMessage);
            
            reject(err);
          }
        }, 300); // 300ms delay
      });
    } catch (err) {
      console.error('Error setting up delayed clear cart:', err);
      setError(err);
      throw err;
    }
  };

  // Context value
  const value = {
    cartItems,
    loading,
    error,
    ...calculateTotals(),
    formatPrice,
    addToCart,
    updateCartItem,
    removeCartItem,
    clearCart,
    refreshCart: fetchCartItems
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook to use the cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
